<template>
  <div class="form-container">
    <p class="form-description">
      Share a "request video" link to allow users to record Zight videos without needing a Zight account. The videos they record will be stored in your account.
      You can manage and edit them later.
    </p>

    <FormKit type="form" @submit="handleFormSubmit" :actions="false" v-slot="{ state }">
      <!-- Name Field -->
      <div class="form-group">
        <FormKit
          type="text"
          name="name"
          label="Name"
          :placeholder="organizationStore.requestContentSettings?.defaultTitle || getSystemDefaultName() || 'Enter a name for this request link'"
          v-model="formData.name"
          validation="length:0,100"
          validation-visibility="live"
          :validation-messages="{
            length: 'Name cannot exceed 100 characters',
          }"
          data-testid="name-input"
        />
      </div>

      <!-- Message Field -->
      <div class="form-group">
        <FormKit
          type="textarea"
          name="message"
          label="Message"
          help="This message will be shown to users when they visit your request link."
          :placeholder="organizationStore.requestContentSettings?.defaultDescription || 'Enter a message for users'"
          v-model="formData.message"
          rows="4"
          validation="length:0,500"
          validation-visibility="live"
          :validation-messages="{
            length: 'Message cannot exceed 500 characters',
          }"
        />
      </div>

      <!-- Custom ID Field -->
      <div class="form-group">
        <FormKit
          type="text"
          name="customId"
          label="Custom ID"
          placeholder="Optional custom identifier"
          v-model="formData.customId"
          validation="length:0,50|matches:/^[a-zA-Z0-9-_]*$/"
          validation-visibility="live"
          :validation-messages="{
            length: 'Custom ID cannot exceed 50 characters',
            matches: 'Only letters, numbers, hyphens, and underscores allowed',
          }"
        />
      </div>

      <!-- Collect Logs Toggle -->
      <div v-if="logCollectionEnabled" class="form-group collect-logs-group">
        <div class="switch-container">
          <label class="switch-wrapper">
            <input type="checkbox" v-model="formData.collectLogs" class="switch-input" />
            <span class="switch-slider"></span>
          </label>
          <span class="switch-label">Ask to collect logs</span>
        </div>

        <div v-if="formData.collectLogs" class="collect-logs-info">
          <div v-if="!requestContentLogEnabled" class="warning-message">
            <Icon name="general.warning" size="sm" class="warning-icon" />
            <span
              >You need to add a log collection URL to use this feature.
              <a href="#" class="warning-link" @click.prevent="gotoRequestContentSettings">Configure settings</a></span
            >
          </div>
          <div v-else class="log-collection-domain">
            <span>Logs will be collected from:</span>
            <span class="domain-text">
              <strong>{{ requestContentLogDomain }}</strong>
            </span>
          </div>
        </div>
      </div>

      <!-- Expiration and Collection Row -->
      <div class="form-row-grid">
        <div class="form-field-wrapper">
          <FormKit
            type="menuselect"
            name="expiresOption"
            label="Expires"
            v-model="formData.expiresOption"
            :options="expirationOptions"
            placeholder="Select expiration"
            outer-class="inline-field"
            data-testid="expires-select"
          />
        </div>

        <div class="form-field-wrapper">
          <FormKit
            type="menuselect"
            name="collectionSelection"
            label="Collection"
            v-model="collectionSelection"
            :options="collectionSelectOptions"
            placeholder="Select collection"
            outer-class="inline-field"
            max-width="160px"
            @input="handleCollectionSelectionChange"
            data-testid="collection-select"
          />
        </div>
      </div>

      <!-- Submit Buttons -->
      <div class="form-actions">
        <div class="flex gap-3">
          <Button type="ghost" variant="black" text="Cancel" @click="$emit('close')" data-testid="cancel-button" />
          <Button
            type="solid"
            variant="blue"
            :text="submitButtonText"
            :disabled="getSubmitDisabledState(state)"
            :class="{ 'pointer-events-none': getSubmitDisabledState(state) }"
            submit
            data-testid="save-button"
          />
        </div>
        <!-- Edit URL Button (only for edit mode) -->
        <Button v-if="edit" type="link" variant="blue" text="Edit URL" icon="general.pencil" @click="$emit('editUrl')" data-testid="edit-url-button" />
      </div>

      <p v-if="!edit" class="form-note">Once created, the link is copied to your clipboard.</p>
    </FormKit>
  </div>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { storeToRefs } from 'pinia';
import { Button, Icon } from '@/storybook';
import { FormKit } from '@formkit/vue';
import type { RequestContent } from '../../types';
import { useRequestContentModalForm } from '../../composables/useRequestContentModalForm';
import { useRequestContentCollectionSelection } from '../../composables/useRequestContentCollectionSelection';
import type { CollectionItemInterface } from '@/store/useCollectionsStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useFeatures } from '@/composables/useFeatures';

interface Props {
  edit: boolean;
  item?: RequestContent | null;
  domain: string;
  selectedCollection?: CollectionItemInterface | null;
}

const props = defineProps<Props>();

interface Emits {
  save: [item: RequestContent];
  close: [];
  showCollectionPicker: [];
  editUrl: [];
}

const emit = defineEmits<Emits>();

const organizationStore = useOrganizationStore();
const { requestContentLogEnabled, requestContentLogDomain } = storeToRefs(organizationStore);
const { getFeature } = useFeatures();

const { formData, expirationOptions, submitButtonText, isNameValid, handleSubmit } = useRequestContentModalForm({
  edit: props.edit,
  item: props.item,
  domain: props.domain,
});

// Create a function to check if submit should be disabled based on FormKit state
const getSubmitDisabledState = (formState: any) => {
  if (!props.edit) {
    // For new forms, only check form validity and log collection requirements
    return !formState.valid || !isNameValid.value || (formData.collectLogs && !requestContentLogEnabled.value);
  }

  // For edit forms, check all conditions including form dirty state
  return !formState.valid || !formState.dirty || !isNameValid.value || (formData.collectLogs && !requestContentLogEnabled.value);
};

// Collection selection composable
const {
  collectionSelection,
  collectionSelectOptions,
  handleCollectionSelectionChange: handleCollectionChange,
  updateCollectionFromPicker,
  getCollectionIdForSaving,
} = useRequestContentCollectionSelection(props.item?.collectionId);

// Simple system default name function
const getSystemDefaultName = () => {
  if (organizationStore.name) {
    return `${organizationStore.name} needs your help with a short video.`;
  }
  return 'Record a short video.';
};

// Initialize with existing collection if editing
if (props.item?.collectionId && props.edit) {
  // Collection will be initialized automatically by the composable
}

// Collect logs feature flag and settings
const logCollectionEnabled = getFeature('enable-log-collection', false);

// Navigate to settings
const gotoRequestContentSettings = () => {
  const orgId = organizationStore.id;
  if (orgId) {
    window.location.href = `/organizations/${orgId}/settings/request_video`;
  }
};

// Collection selection handled by composable

const handleCollectionSelectionChange = (value: string) => {
  handleCollectionChange(value, () => emit('showCollectionPicker'));
};

const handleFormSubmit = (submittedData: any) => {
  const cleanData = handleSubmit(submittedData, getCollectionIdForSaving, getSystemDefaultName, organizationStore.requestContentSettings);
  emit('save', cleanData);
};

// Update collection from picker when prop changes
watch(
  () => props.selectedCollection,
  newCollection => {
    if (newCollection) {
      updateCollectionFromPicker(newCollection);
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.form-container {
  @apply flex flex-col gap-8;
}

.form-description {
  @apply text-sm text-zinc-500 dark:text-zinc-400 mb-2 leading-relaxed;
}

.form-group {
  @apply mb-8;
}

.form-label {
  @apply block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2;
}

.help-text {
  @apply text-xs text-zinc-500 dark:text-zinc-400 mb-2;
}

.form-actions {
  @apply flex justify-between items-center pt-8 mt-8 border-t border-zinc-200 dark:border-zinc-700;
}

.form-note {
  @apply text-sm text-zinc-500 dark:text-zinc-400 mt-4 mb-0;
}

.form-row {
  @apply flex flex-row gap-4 items-start mb-6 mt-6;
}

.form-row-grid {
  @apply grid gap-6 items-start mb-8 mt-8;
  grid-template-columns: 200px 200px; /* Fixed widths: 200px for Expires, 200px for Collection */
  max-width: 75%; /* Reduce overall width to 75% of container */
}

.form-field-wrapper {
  @apply min-w-0 max-w-full; /* Ensures the field can shrink below its content size and respect container width */
  /* Don't clip overflow here - let FormKit handle dropdown positioning */
}

.form-section {
  @apply flex flex-col gap-2 flex-1;
}

.form-label-inline {
  @apply text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2;
}

.inline-field {
  @apply flex-1;
}

/* Inline edit styles removed - using standard FormKit fields */

/* Bootstrap-style switch styles */
.switch-container {
  @apply flex items-center gap-3 mb-3;
}

/* Collect logs section spacing */
.collect-logs-group {
  @apply pt-6 border-t border-zinc-200 dark:border-zinc-700;
}

.switch-wrapper {
  @apply relative inline-block cursor-pointer;
}

.switch-input {
  @apply sr-only;
}

.switch-slider {
  @apply relative inline-block w-12 h-6
         bg-zinc-300 dark:bg-zinc-600
         rounded-full transition-all duration-200
         before:content-[''] before:absolute before:top-0.5 before:left-0.5
         before:w-5 before:h-5 before:bg-white before:rounded-full
         before:transition-transform before:duration-200 before:shadow-md;
}

.switch-input:checked + .switch-slider {
  @apply bg-blue-500 dark:bg-blue-400;
}

.switch-input:checked + .switch-slider:before {
  transform: translateX(24px);
}

.switch-input:focus + .switch-slider {
  @apply ring-2 ring-blue-500 ring-opacity-50;
}

.switch-label {
  @apply text-base text-zinc-900 dark:text-zinc-100 m-0;
}

/* Collect logs info styles */
.collect-logs-info {
  @apply mt-3 p-3 bg-zinc-50 dark:bg-zinc-700 rounded-md;
}

.warning-message {
  @apply flex items-start gap-2 text-sm text-amber-600 dark:text-amber-400
         bg-amber-50 dark:bg-amber-900/20 p-3 rounded
         border border-amber-200 dark:border-amber-800;
}

.warning-icon {
  @apply flex-shrink-0 mt-0.5;
}

.warning-link {
  @apply font-medium underline hover:text-amber-700 dark:hover:text-amber-300 transition-colors;
}

.log-collection-domain {
  @apply text-sm text-zinc-600 dark:text-zinc-300;
}

.domain-text {
  @apply ml-1 text-zinc-800 dark:text-zinc-200;
}

/* Simple FormKit field width constraints for grid layout */
.form-field-wrapper :deep(.formkit-outer),
.form-field-wrapper :deep(.formkit-wrapper),
.form-field-wrapper :deep(.formkit-input) {
  @apply w-full;
}
</style>
